import { useEffect, useCallback, useState } from 'react'
import { useBeforeUnload } from 'react-router-dom'

/**
 * Hook to warn users when they try to navigate away from a form with unsaved changes.
 *
 * Handles browser close/refresh via beforeunload event.
 *
 * Note: In-app navigation blocking via useBlocker is not supported with BrowserRouter.
 * The app would need to migrate to createBrowserRouter/RouterProvider to enable it.
 *
 * Usage:
 * ```tsx
 * const [isDirty, setIsDirty] = useState(false)
 * useUnsavedChanges(isDirty)
 *
 * // Set isDirty to true when form changes
 * onChange={() => setIsDirty(true)}
 *
 * // Reset isDirty after successful save
 * onSave(() => { setIsDirty(false); ... })
 * ```
 */
export function useUnsavedChanges(hasUnsavedChanges: boolean) {
  // Track if user is navigating away
  const [isNavigating, setIsNavigating] = useState(false)

  // Warn on browser close/refresh
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          e.preventDefault()
          // Most browsers ignore custom messages, but setting returnValue is required
          e.returnValue = ''
        }
      },
      [hasUnsavedChanges]
    )
  )

  // Note: useBlocker from react-router-dom only works with data routers
  // (createBrowserRouter/RouterProvider). Since this app uses BrowserRouter,
  // in-app navigation blocking is handled via beforeunload only.
  // To add in-app navigation blocking, migrate to data router pattern.

  // Clean up navigation state on unmount
  useEffect(() => {
    return () => {
      setIsNavigating(false)
    }
  }, [])
}

/**
 * Helper to compare form data with initial values to determine dirty state.
 * Useful for forms that need to track if any field has changed from initial values.
 */
export function isFormDirty<T extends Record<string, unknown>>(
  current: T,
  initial: T
): boolean {
  return Object.keys(current).some((key) => {
    const currentVal = current[key]
    const initialVal = initial[key]

    // Handle arrays (like domains in org settings)
    if (Array.isArray(currentVal) && Array.isArray(initialVal)) {
      return JSON.stringify(currentVal) !== JSON.stringify(initialVal)
    }

    return currentVal !== initialVal
  })
}
